import IActionMap from '../../../../Actions/Interfaces/IActionMap';
import IFocusableActions from '../../../../Actions/Interfaces/IFocusableActions';
import FocusableNodeType, {TFocusableNodeType} from '../../../../Tree/Constants/FocusableNodeType';
import FocusableNode from '../../../../Tree/FocusableNode';
import createDefaultActions from '../../../../Tree/Util/createDefaultActions';

import UseFocusMutableState from './UseFocusMutableState';

type TAllMutableStates = UseFocusMutableState<'GRID'> &
    UseFocusMutableState<'HORIZONTAL_LIST'> &
    UseFocusMutableState<'VERTICAL_LIST'> &
    UseFocusMutableState<'VIRTUAL_HORIZONTAL_LIST'> &
    UseFocusMutableState<'VIRTUAL_VERTICAL_LIST'> &
    UseFocusMutableState<'LEAF'>;

const mapUseFocusHookToNode = (
    id: string,
    parentId: string,
    mutableState: UseFocusMutableState<TFocusableNodeType>,
    focus: () => void,
    blur: () => void,
    type: TFocusableNodeType = FocusableNodeType.LEAF
): FocusableNode => {
    const node = new FocusableNode();
    const options = mutableState.options as TAllMutableStates['options'];

    node.id = id;
    node.parentId = parentId;
    node.type = type;

    node.blur = e => {
        options.onBlur?.(e);

        if (e.target !== node || !mutableState.isRendered) return;

        blur();
    };

    node.focus = e => {
        options.onFocus?.(e);

        if (e.target !== node || !mutableState.isRendered) return;

        focus();
    };

    node.up = e => options.onUp?.(e);
    node.down = e => options.onDown?.(e);
    node.left = e => options.onLeft?.(e);
    node.right = e => options.onRight?.(e);
    node.select = e => options.onSelect?.(e);
    node.focuswillchange = e => options.onFocusWillChange?.(e);

    const descriptors: {[K in keyof FocusableNode]?: PropertyDescriptor} = {
        actions: {
            get: (): IFocusableActions =>
                Object.assign<IFocusableActions, IActionMap | undefined>(createDefaultActions(), options.actions),
        },
        basisOverride: {
            get: () => options.focusableBasis ?? FocusableNode.defaultOptions.basisOverride,
        },
        anchorsOverride: {
            get: () => options.focusableAnchors ?? FocusableNode.defaultOptions.anchorsOverride,
        },
        delegateChangeFocus: {
            get: () => options.delegateChangeFocus ?? FocusableNode.defaultOptions.delegateChangeFocus,
        },
        isFocusDisabled: {
            get: () => options.disableFocus ?? FocusableNode.defaultOptions.isFocusDisabled,
        },
        indexOverride: {
            get: () => options.focusableIndex ?? FocusableNode.defaultOptions.indexOverride,
        },
        hasFocusableElement: {
            get: () => mutableState.focusableElement !== null,
        },
        justify: {
            get: () => options.justify ?? FocusableNode.defaultOptions.justify,
        },
        finalGridRowAlign: {
            get: () => options.finalGridRowAlign ?? FocusableNode.defaultOptions.finalGridRowAlign,
        },
        columns: {
            get: () => options.columns ?? FocusableNode.defaultOptions.columns,
        },
        loop: {
            get: () => options.loop ?? FocusableNode.defaultOptions.loop,
        },
        throttleDurationMs: {
            get: () => options.throttleDurationMs ?? FocusableNode.defaultOptions.throttleDurationMs,
        },
        virtualContainerItemsLength: {
            get: () => options.itemsLength ?? FocusableNode.defaultOptions.virtualContainerItemsLength,
        },
        virtualContainerStartIndex: {
            get: () => options.startIndex ?? FocusableNode.defaultOptions.virtualContainerStartIndex,
        },
        virtualFocusedIndex: {
            get: () => options.virtualFocusedIndex ?? FocusableNode.defaultOptions.virtualFocusedIndex,
        },
    };

    Object.defineProperties(node, descriptors as PropertyDescriptorMap);

    return node;
};

export default mapUseFocusHookToNode;
